import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Help.css';
import {
  FaBookOpen,
  FaBlog,
  FaCode,
  FaLock,
  FaVideo,
  FaNewspaper,
  FaCopy,
  FaCalendarAlt,
} from 'react-icons/fa';

const Help = () => {
  const [copiedText, setCopiedText] = useState('');
  const [calendlyUrl, setCalendlyUrl] = useState('');

  // Effect to set Calendly URL based on theme
  useEffect(() => {
    const updateCalendlyUrl = () => {
      const theme = document.documentElement.getAttribute('data-bs-theme');
      const isLight = theme === 'light';

      const bgColor = isLight ? 'ffffff' : '1a1a1a';
      const textColor = isLight ? '17202e' : 'ffffff';

      setCalendlyUrl(
        `https://calendly.com/lumiwealth/botspot-call?background_color=${bgColor}&text_color=${textColor}`,
      );
    };

    updateCalendlyUrl();

    // Watch for theme changes
    const observer = new MutationObserver(updateCalendlyUrl);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-bs-theme'],
    });

    return () => observer.disconnect();
  }, []);

  // Effect to load Calendly script
  useEffect(() => {
    // Create Calendly script element
    const head = document.querySelector('head');
    const script = document.createElement('script');
    script.setAttribute('src', 'https://assets.calendly.com/assets/external/widget.js');
    script.setAttribute('type', 'text/javascript');
    script.async = true;

    // Append script to head
    head.appendChild(script);

    // Clean up function to remove script when component unmounts
    return () => {
      if (head && script) {
        head.removeChild(script);
      }
    };
  }, []); // Empty dependency array ensures this runs once on mount

  // Function to copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        // Set the copied text to show feedback
        setCopiedText(text);
        // Clear the copied text after 2 seconds
        setTimeout(() => {
          setCopiedText('');
        }, 2000);
      },
      (err) => {
        console.error('Could not copy text: ', err);
      },
    );
  };

  // Helper function to render table rows with copy buttons
  const renderTableRowWithCopy = (key, description, example) => (
    <tr key={key}>
      <td>
        <div className="help-key-container">
          <span className="help-key-text">{key}</span>
          <button
            className={`help-copy-button ${copiedText === key ? 'help-copied' : ''}`}
            onClick={() => copyToClipboard(key)}
            title="Copy to clipboard"
          >
            <FaCopy className="help-copy-icon" />
            <span className="help-copy-button-text">{copiedText === key ? 'Copied!' : 'Copy'}</span>
          </button>
        </div>
      </td>
      <td>{description}</td>
      <td>{example}</td>
    </tr>
  );

  return (
    <div className="help-center-container">
      <div className="help-center-header">
        <h1>
          <span className="help-highlight-text">❓</span> Help Center
        </h1>
        <p>
          Welcome to BotSpot's Help section. Here you'll find guides, video walkthroughs, and
          articles explaining our AI Bot Builder. Whether you're setting up your first paper-trading
          bot or reviewing a strategy, these resources provide product instructions and risk
          context. They do not promise trading results.
        </p>
      </div>

      {/* Book a Call Section with Calendly */}
      <section className="help-content-section help-with-glow">
        <div className="help-section-header">
          <FaCalendarAlt className="help-section-icon" />
          <h2>Book a Consultation Call</h2>
        </div>
        <p>
          Have questions or need personalized assistance? Schedule a call with our team of experts
          who can help you get the most out of BotSpot:
        </p>
        <div className="help-calendly-container">
          {/* Calendly inline widget - removed the script tag */}
          {calendlyUrl && (
            <div
              className="calendly-inline-widget"
              data-url={calendlyUrl}
              style={{ minWidth: '320px', height: '700px' }}
            ></div>
          )}
        </div>
      </section>

      {/* Videos Section with embedded YouTube players */}
      <section className="help-content-section help-with-glow">
        <div className="help-section-header">
          <FaVideo className="help-section-icon" />
          <h2>Learning Videos</h2>
        </div>
        <p>Watch these tutorials to get started with BotSpot and learn advanced techniques:</p>
        <div className="help-videos-grid">
          <div className="help-video-item">
            <iframe
              width="100%"
              height="315"
              src="https://www.youtube.com/embed/PstSNLbZ-q4"
              title="AI Bot Builder backtest walkthrough"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
            <div className="help-video-title">
              <h3>AI Bot Builder backtest walkthrough</h3>
            </div>
            <div className="help-video-description">
              <p>
                This historical workshop demonstrates how to create and backtest an options
                strategy. Any figures shown are hypothetical backtest results, not live performance,
                and do not predict future results. Options and automated trading can lose the full
                amount at risk.
              </p>
            </div>
          </div>

          <div className="help-video-item">
            <iframe
              width="100%"
              height="315"
              src="https://www.youtube.com/embed/UNAvkWYVRJs"
              title="How to Automate Trading with AI – Generate & Backtest Bots Easily!"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
            <div className="help-video-title">
              <h3>How to Automate Trading with AI – Generate & Backtest Bots Easily!</h3>
            </div>
            <div className="help-video-description">
              <p>
                This video offers a general overview of BotSpot, demonstrating the core features of
                our platform. Watch to learn how you can quickly generate trading bots and
                efficiently backtest them, streamlining your trading workflow.
              </p>
            </div>
          </div>

          <div className="help-video-item">
            <iframe
              width="100%"
              height="315"
              src="https://www.youtube.com/embed/3F6RKqA7xyQ"
              title="New AI Bot Builder Workshop - Wednesday, Feb 26, 2025"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
            <div className="help-video-title">
              <h3>New AI Bot Builder Workshop - Wednesday, Feb 26, 2025</h3>
            </div>
            <div className="help-video-description">
              <p>
                This full-length workshop provides a step-by-step guide to AI Bot Builder features,
                testing practices, and risk controls. It does not provide investment advice or
                promise performance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Bot Builder Articles Section */}
      <section className="help-content-section help-with-glow">
        <div className="help-section-header">
          <FaNewspaper className="help-section-icon" />
          <h2>AI Bot Builder Articles</h2>
        </div>
        <p>Explore our comprehensive guides on building trading bots:</p>
        <div className="help-cards-container">
          <div className="help-card">
            <div className="help-card-content">
              <h3>
                <Link to="/blog/using-ai-to-make-ai-profitable-swing-trading-bot-in-seconds">
                  Stock Trading Bot with AI
                </Link>
              </h3>
              <p>
                Learn how to create a swing trading bot using TQQQ. This strategy leverages the
                200-day moving average to capture market trends and delivered a back-tested return
                of 42%. Discover key indicators, entry and exit signals, and risk management
                insights.
              </p>
              <div className="help-card-footer">
                <Link
                  className="help-card-button"
                  to="/blog/using-ai-to-make-ai-profitable-swing-trading-bot-in-seconds"
                >
                  Read Article
                </Link>
              </div>
            </div>
          </div>
          <div className="help-card">
            <div className="help-card-content">
              <h3>
                <a
                  href="https://lumiwealth.com/how-to-build-a-high-performing-options-trading-bot-with-ai-under-a-minute/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  PLTR Options Trading Bot with AI
                </a>
              </h3>
              <p>
                Discover how to create a complex options trading bot using AI. This guide explains
                how to build a bot that leverages intricate algorithmic strategies for options
                trading and utilizes Polygon as a reliable data source. Turn a modest $1000
                investment into impressive gains as demonstrated by back-test results.
              </p>
              <div className="help-card-footer">
                <a
                  className="help-card-button"
                  href="https://lumiwealth.com/how-to-build-a-high-performing-options-trading-bot-with-ai-under-a-minute/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read Article
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog and Documentation in a side-by-side layout */}
      <div className="help-dual-section-container">
        {/* Blog Section */}
        <section className="help-content-section help-with-glow help-half-section">
          <div className="help-section-header">
            <FaBlog className="help-section-icon" />
            <h2>Our Blog</h2>
          </div>
          <p>
            Our blog features numerous articles explaining how to use the AI Bot Builder, detailing
            how Lumibot works (with code examples), and more.
          </p>
          <div className="help-card-footer">
            <Link to="/blog" className="help-card-button">
              Visit our Blog
            </Link>
          </div>
        </section>

        {/* Documentation Section */}
        <section className="help-content-section help-with-glow help-half-section">
          <div className="help-section-header">
            <FaBookOpen className="help-section-icon" />
            <h2>Documentation</h2>
          </div>
          <p>
            Our technical documentation provides comprehensive information about Lumibot, our
            powerful automated trading framework that powers BotSpot.
          </p>
          <div className="help-card-footer">
            <a
              href="https://lumibot.lumiwealth.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="help-card-button"
            >
              View Documentation
            </a>
          </div>
        </section>
      </div>

      {/* Secrets & Environment Variables Section */}
      <section className="help-content-section help-with-glow">
        <div className="help-section-header">
          <FaLock className="help-section-icon" />
          <h2>Secrets & Environment Variables</h2>
        </div>
        <p>
          When deploying your trading bot, you'll need to configure certain secrets and environment
          variables depending on your broker and requirements. These settings enable your bot to
          connect securely to trading platforms and customize its behavior.
        </p>

        <h3>Broker Configuration</h3>
        <p>Choose the broker configuration that matches the platform you're using:</p>

        <div className="help-accordion">
          <div className="help-broker-section">
            <h4>Tradier Configuration</h4>
            <p>
              Tradier offers trading for stocks, options, and soon futures, with an excellent
              $10/month plan for commission-free options trading.
              <a href="https://tradier.com/" target="_blank" rel="noopener noreferrer">
                {' '}
                Create a Tradier account
              </a>
            </p>
            <div className="help-secrets-table">
              <table>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Description</th>
                    <th>Example Value</th>
                  </tr>
                </thead>
                <tbody>
                  {renderTableRowWithCopy(
                    'TRADIER_ACCESS_TOKEN',
                    'Your Access Token from Tradier',
                    'qTRz3zUrl9244AHUw4AoyAPgvYra',
                  )}
                  {renderTableRowWithCopy(
                    'TRADIER_ACCOUNT_NUMBER',
                    'Your Account Number from Tradier',
                    'VA12204793',
                  )}
                  {renderTableRowWithCopy(
                    'TRADIER_IS_PAPER',
                    'Set to "True" for paper trading, "False" for real money trading',
                    'True',
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="help-broker-section">
            <h4>Alpaca Configuration</h4>
            <p>
              Alpaca is a commission-free broker designed for API trading, supporting stocks,
              crypto, and soon options.
              <a href="https://alpaca.markets/" target="_blank" rel="noopener noreferrer">
                {' '}
                Create an Alpaca account
              </a>
            </p>

            <div className="help-oauth-recommended">
              <h5>🔐 OAuth Authentication (Recommended)</h5>
              <p>
                Use our secure OAuth integration for the best experience. No need to manually manage
                API keys!
              </p>
              <div className="help-oauth-steps">
                <ol>
                  <li>Click "Connect with Alpaca OAuth" on the deployment page</li>
                  <li>Authorize LumiVest to access your Alpaca account</li>
                  <li>Your bot is automatically configured with secure access</li>
                </ol>
              </div>
            </div>

            <div className="help-manual-fallback">
              <h5>🔑 Manual API Keys (Fallback)</h5>
              <p>If you prefer to manually enter your API credentials:</p>
              <div className="help-secrets-table">
                <table>
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Description</th>
                      <th>Example Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderTableRowWithCopy(
                      'ALPACA_API_KEY',
                      'Your API key from your Alpaca brokerage account',
                      'YOUR_ALPACA_API_KEY',
                    )}
                    {renderTableRowWithCopy(
                      'ALPACA_API_SECRET',
                      'Your secret key from your Alpaca brokerage account',
                      'YOUR_ALPACA_API_SECRET',
                    )}
                    {renderTableRowWithCopy(
                      'ALPACA_IS_PAPER',
                      'Set to "True" for paper trading, "False" for real money trading',
                      'True',
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="help-broker-section">
            <h4>Coinbase Configuration</h4>
            <p>
              Coinbase is a cryptocurrency broker available throughout the United States, including
              New York, with a wide range of cryptocurrencies.
              <a href="https://www.coinbase.com/" target="_blank" rel="noopener noreferrer">
                {' '}
                Create a Coinbase account
              </a>
            </p>
            <div className="help-secrets-table">
              <table>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Description</th>
                    <th>Example Value</th>
                  </tr>
                </thead>
                <tbody>
                  {renderTableRowWithCopy(
                    'COINBASE_API_KEY_NAME',
                    'Your API key name/identifier for Coinbase',
                    'organizations/YOUR_ORG_ID/apiKeys/YOUR_KEY_ID',
                  )}
                  {renderTableRowWithCopy(
                    'COINBASE_PRIVATE_KEY',
                    'Your private key for Coinbase',
                    '-----BEGIN EC PRIVATE KEY-----\\nYOUR_PRIVATE_KEY_MATERIAL\\n-----END EC PRIVATE KEY-----',
                  )}
                  {renderTableRowWithCopy(
                    'COINBASE_API_PASSPHRASE',
                    'Your API passphrase for Coinbase (Optional)',
                    'YOUR_OPTIONAL_PASSPHRASE',
                  )}
                  {renderTableRowWithCopy(
                    'COINBASE_IS_SANDBOX',
                    'Set to "True" for sandbox (paper trading), "False" for real money trading',
                    'False',
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="help-broker-section">
            <h4>Kraken Configuration</h4>
            <p>
              Kraken offers very low fees and a wide range of cryptocurrencies, ideal for crypto
              trading.
              <a href="https://www.kraken.com/" target="_blank" rel="noopener noreferrer">
                {' '}
                Create a Kraken account
              </a>
            </p>
            <div className="help-secrets-table">
              <table>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Description</th>
                    <th>Example Value</th>
                  </tr>
                </thead>
                <tbody>
                  {renderTableRowWithCopy(
                    'KRAKEN_API_KEY',
                    'Your API key from Kraken',
                    'XyZ1234567890abcdef',
                  )}
                  {renderTableRowWithCopy(
                    'KRAKEN_API_SECRET',
                    'Your API secret for Kraken',
                    'abcdef1234567890abcdef1234567890abcdef1234',
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="help-broker-section">
            <h4>Interactive Brokers Configuration</h4>
            <p>
              Interactive Brokers is ideal for international users with a wide array of asset
              classes.
              <a
                href="https://www.interactivebrokers.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {' '}
                Create an Interactive Brokers account
              </a>
            </p>
            <div className="help-secrets-table">
              <table>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Description</th>
                    <th>Example Value</th>
                  </tr>
                </thead>
                <tbody>
                  {renderTableRowWithCopy(
                    'IB_USERNAME',
                    'Your Interactive Brokers username',
                    'user123',
                  )}
                  {renderTableRowWithCopy(
                    'IB_PASSWORD',
                    'Your Interactive Brokers password',
                    'password123',
                  )}
                  {renderTableRowWithCopy(
                    'ACCOUNT_ID',
                    '(Optional) An Interactive Brokers subaccount to trade on',
                    'U17369206',
                  )}
                  {renderTableRowWithCopy(
                    'IB_API_URL',
                    '(Optional) The URL of your self-hosted Interactive Brokers REST API',
                    'https://localhost:8000',
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="help-broker-section">
            <h4>Interactive Brokers-Legacy Configuration</h4>
            <p>Legacy version that uses the TWS API, maintained for compatibility purposes.</p>
            <div className="help-secrets-table">
              <table>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Description</th>
                    <th>Example Value</th>
                  </tr>
                </thead>
                <tbody>
                  {renderTableRowWithCopy(
                    'INTERACTIVE_BROKERS_PORT',
                    'Socket port for Interactive Brokers',
                    '7497',
                  )}
                  {renderTableRowWithCopy(
                    'INTERACTIVE_BROKERS_CLIENT_ID',
                    'Client ID for Interactive Brokers',
                    '123456',
                  )}
                  {renderTableRowWithCopy(
                    'INTERACTIVE_BROKERS_IP',
                    'IP address for Interactive Brokers (defaults to "127.0.0.1")',
                    '127.0.0.1',
                  )}
                  {renderTableRowWithCopy(
                    'IB_SUBACCOUNT',
                    'Subaccount for Interactive Brokers',
                    'Subaccount1',
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="help-broker-section">
            <h4>Schwab Configuration</h4>
            <p>
              Charles Schwab provides API access for automated trading and market data through its
              developer platform.
              <a href="https://developer.schwab.com" target="_blank" rel="noopener noreferrer">
                {' '}
                Create a Schwab developer account
              </a>
            </p>
            <div className="help-secrets-table">
              <table>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Description</th>
                    <th>Example Value</th>
                  </tr>
                </thead>
                <tbody>
                  {renderTableRowWithCopy(
                    'SCHWAB_API_KEY',
                    'Your Schwab API key obtained from the developer dashboard',
                    'your_api_key_here',
                  )}
                  {renderTableRowWithCopy(
                    'SCHWAB_SECRET',
                    'Your Schwab API secret obtained from the developer dashboard',
                    'your_api_secret_here',
                  )}
                  {renderTableRowWithCopy(
                    'SCHWAB_ACCOUNT_NUMBER',
                    'Your Schwab account number used for trading',
                    '123456789',
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <h3 className="help-env-vars-heading">General Environment Variables</h3>
        <p className="help-env-vars-intro">
          Besides broker-specific configurations, your trading bot also requires the following
          general environment variables. These control core functionality such as performance
          tracking, backtesting parameters, and notification settings:
        </p>

        <div className="help-broker-section help-general-env-section">
          <h4>Required and Optional Variables</h4>
          <p>
            In addition to broker-specific settings, the following environment variables are needed
            for all strategies. These configure core functionality like performance tracking,
            backtesting parameters, and data sources:
          </p>
          <div className="help-secrets-table">
            <table>
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Description</th>
                  <th>Example Value</th>
                </tr>
              </thead>
              <tbody>
                {renderTableRowWithCopy(
                  'LUMIWEALTH_API_KEY',
                  "Your API key from BotSpot.trade to track your bot's performance",
                  '694rr2c8d9234b43a40fab494a79f5634ghd4f39d44ccf2e',
                )}
                {renderTableRowWithCopy(
                  'LIVE_CONFIG',
                  'Your live config file (only for strategies with multiple configurations)',
                  'paper_1',
                )}
                {renderTableRowWithCopy(
                  'IS_BACKTESTING',
                  '(Optional) Set to "True" for backtesting, "False" for live trading',
                  'False',
                )}
                {renderTableRowWithCopy(
                  'BACKTESTING_START',
                  '(Optional) Start date for backtesting (YYYY-MM-DD)',
                  '2025-01-01',
                )}
                {renderTableRowWithCopy(
                  'BACKTESTING_END',
                  '(Optional) End date for backtesting (YYYY-MM-DD)',
                  '2025-01-31',
                )}
                {renderTableRowWithCopy(
                  'POLYGON_API_KEY',
                  '(Optional) Your API key from Polygon (for backtesting)',
                  'a7py0zIdhxde6QkX8OjjKNp7cD87hwKU',
                )}
                {renderTableRowWithCopy(
                  'DISCORD_WEBHOOK_URL',
                  '(Optional) Discord webhook URL for notifications',
                  'https://discord.com/api/webhooks/123456789/',
                )}
                {renderTableRowWithCopy(
                  'DB_CONNECTION_STR',
                  '(Optional) Connection string to your account history database',
                  'sqlite:///account_history.db',
                )}
                {renderTableRowWithCopy(
                  'STRATEGY_NAME',
                  '(Optional) Name of the strategy',
                  'My Strategy',
                )}
                {renderTableRowWithCopy(
                  'MARKET',
                  '(Optional) The market type for the bot (e.g., "24/7")',
                  '24/7',
                )}
                {renderTableRowWithCopy(
                  'POLYGON_MAX_MEMORY_BYTES',
                  '(Optional) Maximum memory for Polygon API during backtesting',
                  '512000000',
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <div className="help-page-footer">
        <p>
          Need more help? Contact our <a href="mailto:support@lumiwealth.com">support team</a>.
        </p>
      </div>
    </div>
  );
};

export default Help;
